
import React, { ReactNode, useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";
import { SubscriptionFeatures } from "@/lib/types";
import { Card } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { TranslationText } from "@/components/i18n/TranslationText";
import { useSystemSettings } from "@/hooks/useSystemSettings";

interface FeatureRouteGuardProps {
  feature: keyof SubscriptionFeatures;
  children: ReactNode;
  redirectTo?: string;
}

export function FeatureRouteGuard({ 
  feature, 
  children, 
  redirectTo = '/settings?tab=subscription' 
}: FeatureRouteGuardProps) {
  const { hasFeature, isLoading, error } = useFeatureFlags();
  const [checked, setChecked] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState<boolean>(true);
  const { getPublicBooleanSetting } = useSystemSettings();
  const navigate = useNavigate();
  
  // Check if subscriptions are enabled
  useEffect(() => {
    const checkSubscriptionsEnabled = async () => {
      try {
        const enabled = await getPublicBooleanSetting('subscriptions_enabled', true);
        setSubscriptionsEnabled(enabled);
      } catch (error) {
        console.error("Error checking if subscriptions are enabled:", error);
        // Default to true if there's an error
        setSubscriptionsEnabled(true);
      }
    };
    
    checkSubscriptionsEnabled();
  }, [getPublicBooleanSetting]);
  
  // Use effect to avoid immediate redirects and implement retry logic
  useEffect(() => {
    if (!isLoading) {
      setChecked(true);
    }
  }, [isLoading]);
  
  // Function to retry feature check
  const handleRetry = () => {
    setChecked(false);
    setRetryCount(prev => prev + 1);
    setTimeout(() => setChecked(true), 500);
  };
  
  // When still loading, render a loader
  if (isLoading || !checked) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Handle errors in feature check
  if (error) {
    return (
      <Alert variant="destructive" className="my-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>
          <TranslationText i18nKey="features.error.title">
            Error checking feature access
          </TranslationText>
        </AlertTitle>
        <AlertDescription className="flex flex-col gap-4">
          <p>
            <TranslationText i18nKey="features.error.description">
              There was an error checking your subscription features. This might be a temporary issue.
            </TranslationText>
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRetry}>
              <TranslationText i18nKey="features.error.retry">
                Retry check
              </TranslationText>
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()}>
              <TranslationText i18nKey="features.error.reload">
                Reload page
              </TranslationText>
            </Button>
            <Button onClick={() => navigate('/')}>
              <TranslationText i18nKey="features.error.dashboard">
                Go to dashboard
              </TranslationText>
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }
  
  // If the user doesn't have access to the feature
  if (!hasFeature(feature)) {
    // If subscriptions are disabled, just redirect to homepage instead
    if (!subscriptionsEnabled) {
      console.log(`Subscriptions disabled and user does not have access to feature: ${feature}, redirecting to homepage`);
      return <Navigate to="/" replace />;
    }
    
    // If subscriptions are enabled, show subscription page as before
    console.log(`User does not have access to feature: ${feature}, redirecting to ${redirectTo}`);
    return <Navigate to={redirectTo} replace />;
  }
  
  // User has access to the feature
  return <>{children}</>;
}
